import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { conversationsAPI } from '../services/api';
import { serializeMessages, signatureOf } from './conversationCodec';

// Attente après le dernier changement avant d'enregistrer (regroupe les rafales)
const SAVE_DEBOUNCE_MS = 800;
// Nouvelles tentatives après un échec réseau ou serveur (5xx, 429)
const RETRY_DELAYS_MS = [2000, 5000, 15000, 30000, 60000];
// keepalive : le navigateur limite le corps à 64 Ko à la fermeture de la page
const KEEPALIVE_MAX_BYTES = 60 * 1024;

// 400 (liste refusée), 401 (contexte), 404 (conversation supprimée), 413 (trop volumineuse) :
// renvoyer la même liste échouerait encore
const isRetryable = (err) => !err.status || err.status >= 500 || err.status === 429 || err.status === 408;

/**
 * Sauvegarde de la conversation active (PUT de la liste complète des messages).
 *
 * - Déclenchée après chaque tour terminé (aucune réponse en cours), avec un délai
 *   de regroupement ; jamais pendant le streaming. La fin d'une réponse part tout de suite :
 *   une fermeture de l'onglet juste après ne la perd pas (réponses Qwen avec leur réflexion,
 *   trop lourdes pour l'envoi de secours en keepalive).
 * - Non bloquante : l'interface n'attend jamais la sauvegarde.
 * - Idempotente : une liste identique à la dernière enregistrée n'est pas renvoyée.
 * - Nouvelle tentative avec délai croissant en cas d'échec réseau ou serveur.
 * - Changement de conversation ou fermeture de la page : l'envoi en attente part aussitôt.
 *
 * status : idle | pending | saving | saved | retrying | error
 */
export function useConversationSync({ enabled, conversationId, messages, isLoading, userContext, onSaved }) {
  const [state, setState] = useState({ status: 'idle', error: null });

  const contextRef = useRef(userContext);
  contextRef.current = userContext;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  // Dernière signature enregistrée, par conversation
  const savedRef = useRef(new Map());
  // Envois en attente, un par conversation (le plus récent) : id → {id, payload, signature}
  const pendingRef = useRef(new Map());
  // Échecs définitifs, renvoyés par « Réessayer »
  const failedRef = useRef(new Map());
  const inFlightRef = useRef(false);
  // Envoi en cours ({id, payload, signature}) : renvoyé en keepalive si la page se ferme avant sa fin
  const inFlightJobRef = useRef(null);
  // Réponse en cours au rendu précédent, et dans quelle conversation (fin de réponse détectée)
  const streamingRef = useRef({ id: null, streaming: false });
  const attemptRef = useRef(0);
  const debounceRef = useRef(null); // {id, timer}
  const retryRef = useRef(null);
  const mountedRef = useRef(true);
  // Conversations supprimées : un envoi encore en vol pour elles est abandonné
  const forgottenRef = useRef(new Set());

  const setStatus = useCallback((status, error = null) => {
    if (mountedRef.current) setState({ status, error });
  }, []);

  // Envoi abandonné (conversation supprimée) : statut selon ce qui reste à faire
  const settleDropped = useCallback(() => {
    if (pendingRef.current.size) setStatus('pending');
    else if (failedRef.current.size) setStatus('error', "La conversation n'a pas pu être enregistrée.");
    else setStatus('idle');
  }, [setStatus]);

  // Envoie le prochain élément en attente ; enchaîne tant qu'il en reste
  const pump = useCallback(async () => {
    if (inFlightRef.current || retryRef.current) return;
    const next = pendingRef.current.values().next();
    if (next.done) return;
    const job = next.value;
    pendingRef.current.delete(job.id);
    if (forgottenRef.current.has(job.id)) {
      pump();
      return;
    }
    if (savedRef.current.get(job.id) === job.signature) {
      setStatus('saved');
      pump();
      return;
    }

    inFlightRef.current = true;
    inFlightJobRef.current = job;
    setStatus('saving');
    try {
      const result = await conversationsAPI.saveMessages(job.id, job.payload, contextRef.current);
      if (forgottenRef.current.has(job.id)) {
        // Supprimée pendant l'envoi : rien à mémoriser ni à afficher
        settleDropped();
      } else {
        savedRef.current.set(job.id, job.signature);
        attemptRef.current = 0;
        if (result?.conversation) onSavedRef.current?.(result.conversation);
        setStatus(pendingRef.current.size ? 'pending' : 'saved');
      }
    } catch (err) {
      if (forgottenRef.current.has(job.id) || err.status === 404) {
        // Conversation supprimée (ici ou dans un autre onglet) : ni nouvel essai ni erreur
        pendingRef.current.delete(job.id);
        failedRef.current.delete(job.id);
        attemptRef.current = 0;
        settleDropped();
      } else {
        // Un envoi plus récent pour la même conversation a pu arriver entre-temps : il prime
        const hasNewer = pendingRef.current.has(job.id);
        if (isRetryable(err) && attemptRef.current < RETRY_DELAYS_MS.length) {
          if (!hasNewer) pendingRef.current.set(job.id, job);
          const delay = RETRY_DELAYS_MS[attemptRef.current];
          attemptRef.current += 1;
          setStatus('retrying', 'Enregistrement impossible, nouvel essai automatique…');
          retryRef.current = setTimeout(() => {
            retryRef.current = null;
            pump();
          }, delay);
        } else {
          // Échec définitif : gardé pour « Réessayer », sans boucle d'envoi
          if (!hasNewer) failedRef.current.set(job.id, job);
          attemptRef.current = 0;
          setStatus('error', err.code === 'QUOTA_EXCEEDED'
            ? "Espace d'historique plein : supprimez d'anciennes conversations."
            : err.status === 413
              ? 'Conversation trop volumineuse pour être enregistrée.'
              : "La conversation n'a pas pu être enregistrée.");
        }
      }
    } finally {
      inFlightRef.current = false;
      inFlightJobRef.current = null;
    }
    pump();
  }, [setStatus, settleDropped]);

  const flushDebounce = useCallback(() => {
    if (!debounceRef.current) return;
    clearTimeout(debounceRef.current.timer);
    debounceRef.current = null;
  }, []);

  /** Programme l'enregistrement de `list` pour la conversation `id`. */
  const schedule = useCallback((id, list, { immediate = false } = {}) => {
    if (!id || forgottenRef.current.has(id)) return;
    const payload = serializeMessages(list);
    const signature = signatureOf(payload);
    if (savedRef.current.get(id) === signature) {
      pendingRef.current.delete(id);
      return;
    }
    failedRef.current.delete(id);
    pendingRef.current.set(id, { id, payload, signature });

    // Délai en cours pour une AUTRE conversation : son envoi part tout de suite
    const otherWaiting = debounceRef.current && debounceRef.current.id !== id;
    flushDebounce();

    if (immediate || otherWaiting) {
      if (immediate) {
        // Envoi explicite : les tentatives différées reprennent maintenant
        clearTimeout(retryRef.current);
        retryRef.current = null;
        attemptRef.current = 0;
      }
      pump();
    }
    if (!immediate) {
      setStatus('pending');
      debounceRef.current = {
        id,
        timer: setTimeout(() => {
          debounceRef.current = null;
          pump();
        }, SAVE_DEBOUNCE_MS),
      };
    }
  }, [flushDebounce, pump, setStatus]);

  /** La conversation vient d'être chargée depuis le serveur : déjà à jour. */
  const markSaved = useCallback((id, list) => {
    savedRef.current.set(id, signatureOf(serializeMessages(list)));
    pendingRef.current.delete(id);
    failedRef.current.delete(id);
    setStatus('saved');
  }, [setStatus]);

  /** Enregistre tout de suite (avant de quitter la conversation). */
  const saveNow = useCallback((id, list) => schedule(id, list, { immediate: true }), [schedule]);

  /**
   * Enregistre tout de suite et attend la fin de l'enregistrement (panier de rapport : le
   * serveur relit les visuels dans la conversation enregistrée). → true si enregistrée.
   */
  const saveAndWait = useCallback((id, list, { timeoutMs = 10000 } = {}) => {
    if (!id) return Promise.resolve(false);
    const signature = signatureOf(serializeMessages(list));
    schedule(id, list, { immediate: true });
    const started = Date.now();
    return new Promise((resolve) => {
      const check = () => {
        if (savedRef.current.get(id) === signature) return resolve(true);
        if (failedRef.current.has(id) || forgottenRef.current.has(id) || Date.now() - started > timeoutMs) return resolve(false);
        setTimeout(check, 100);
      };
      check();
    });
  }, [schedule]);

  /** Conversation supprimée : plus rien à enregistrer pour elle. */
  const forget = useCallback((id) => {
    forgottenRef.current.add(id);
    pendingRef.current.delete(id);
    failedRef.current.delete(id);
    savedRef.current.delete(id);
    if (debounceRef.current?.id === id) flushDebounce();
    if (!pendingRef.current.size && !failedRef.current.size) setStatus('idle');
  }, [flushDebounce, setStatus]);

  /** Bouton « Réessayer » après un échec. */
  const retry = useCallback(() => {
    for (const [id, job] of failedRef.current) {
      if (!pendingRef.current.has(id)) pendingRef.current.set(id, job);
    }
    failedRef.current.clear();
    clearTimeout(retryRef.current);
    retryRef.current = null;
    attemptRef.current = 0;
    pump();
  }, [pump]);

  // ── Tour terminé : enregistrement différé ; fin d'une réponse : tout de suite ──
  useEffect(() => {
    const streaming = messages.some((m) => m.isStreaming);
    const before = streamingRef.current;
    streamingRef.current = { id: conversationId, streaming };
    if (!enabled || !conversationId || isLoading || !messages.length) return;
    if (streaming) return;
    schedule(conversationId, messages, { immediate: before.streaming && before.id === conversationId });
  }, [enabled, conversationId, messages, isLoading, schedule]);

  // ── Fermeture de la page : derniers envois en keepalive si possible ───
  useEffect(() => {
    const onPageHide = () => {
      // Envoi en cours compris : une requête ordinaire peut être coupée avec la page
      const jobs = [...pendingRef.current.values()];
      const enVol = inFlightJobRef.current;
      if (enVol && !pendingRef.current.has(enVol.id) && !forgottenRef.current.has(enVol.id)) jobs.push(enVol);
      for (const job of jobs) {
        if (savedRef.current.get(job.id) === job.signature) continue;
        const size = new Blob([JSON.stringify({ messages: job.payload })]).size;
        if (size > KEEPALIVE_MAX_BYTES) continue;
        conversationsAPI.saveMessages(job.id, job.payload, contextRef.current, { keepalive: true }).catch(() => {});
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current.timer);
      clearTimeout(retryRef.current);
    };
  }, []);

  // Objet stable entre deux changements de statut (barre latérale mémoïsée)
  return useMemo(
    () => ({ ...state, markSaved, saveNow, saveAndWait, retry, forget }),
    [state, markSaved, saveNow, saveAndWait, retry, forget]
  );
}
